import { forwardRef, useCallback, useMemo, useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import cn from 'utils/cn'
import {
  SortByCategories,
  SortDirection,
  TransactionDetailed
} from 'types/entity'
import { defaultExpenseCategories } from 'core/widgets/transaction-detail/resources'
import { useTeamCategories, useUserProfile } from 'core/hooks/data/useDataHook'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import Autosuggest, {
  AutosuggesRef
} from 'core/components/inputs/autosuggest/Autosuggest'
import Popover from 'core/components/dialogs/Popover'
import Menu from 'core/components/dialogs/Menu'
import Button from 'core/components/buttons/Button'
import Icon from 'core/components/icons/Icon'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import debounce from 'lodash.debounce'

interface CategoryOptionProps {
  value?: TransactionDetailed['category']
  view: 'personal' | 'team'
  disabled?: boolean
  readOnly?: boolean
  onChange?: (value: TransactionDetailed['category']) => void
  variant?: 'default' | 'invisible'
  showAllCategories?: boolean
  wrapperClassName?: string
}

const CategoryOption = forwardRef<AutosuggesRef, CategoryOptionProps>(
  (
    {
      value,
      view,
      onChange,
      disabled,
      readOnly,
      variant = 'invisible',
      showAllCategories = false,
      wrapperClassName
    },
    ref
  ) => {
    const { data: team } = useCurrentTeam()
    const { data: user } = useUserProfile()
    const { hasTeam } = useRolesAndPermissions()
    const [query, setQuery] = useState('')
    const [showFiltersMenu, setShowFiltersMenu] = useState(false)
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
    const [sortBy, setSortBy] = useState<SortByCategories>('name')
    const { data, isFetching } = useTeamCategories({
      params: {
        team_token_id: team?.token_id ?? '',
        page_size: 50,
        q: query,
        page: 1,
        sort_by: sortBy,
        sort_direction: sortDirection
      },
      opts: {
        placeholderData: keepPreviousData,
        enabled: !!team?.token_id
      }
    })
    const { data: expense_categories } = data ?? {}
    const { custom_expense_categories } = user ?? {
      custom_expense_categories: []
    }

    const filterByQuery = useCallback(
      (array: string[] = []) => {
        return array.filter((c) => {
          return c.toUpperCase().indexOf(query.toUpperCase()) !== -1
        })
      },
      [query]
    )

    const filterOptions = useMemo(() => {
      return [
        {
          token_id: 'name__asc',
          label: (
            <span>
              Category <span className="mx-3">&#8595;</span>
            </span>
          )
        },
        {
          token_id: 'name__desc',
          label: (
            <span>
              Category <span className="mx-3">&#8593;</span>
            </span>
          )
        },
        {
          token_id: 'created_at__desc',
          label: 'Recently added'
        }
      ]
    }, [])

    const teamOptions = useMemo(() => {
      return expense_categories?.length
        ? [
            {
              groupLabel: (
                <div className="flex items-center justify-between">
                  <span className="leading-5 font-bold">Category</span>
                  <Popover
                    content={
                      <Menu
                        options={filterOptions}
                        value={`${sortBy}__${sortDirection}`}
                        onChange={(_v) => {
                          const [value1, value2] = (_v as string).split('__')
                          setSortBy(value1 as SortByCategories)
                          setSortDirection(value2 as SortDirection)
                          setShowFiltersMenu(false)
                        }}
                      />
                    }
                    show={showFiltersMenu}
                    position="bottom-left"
                  >
                    <Button
                      id="categories-filter-btn"
                      variant="outline"
                      className="p-2!"
                      onClick={() => {
                        setShowFiltersMenu(!showFiltersMenu)
                      }}
                      prefix={<Icon name="sort" height={14} width={14} />}
                    />
                  </Popover>
                </div>
              ),
              items: expense_categories.map((c) => {
                return {
                  token_id: c.name,
                  label: c.name,
                  subLabel: c.code ?? '',
                  prefix: (
                    <Icon name="categories" size={22} color="text-primary" />
                  )
                }
              })
            }
          ]
        : []
    }, [
      expense_categories,
      filterOptions,
      showFiltersMenu,
      sortBy,
      sortDirection
    ])

    const noTeamOptions = useMemo(() => {
      const filteredCustomCategories = filterByQuery(custom_expense_categories)
      const filteredDefaultCategories = filterByQuery(defaultExpenseCategories)

      const customOpts =
        view === 'personal' && filteredCustomCategories.length
          ? {
              groupLabel: <div className="mt-3">Custom categories</div>,
              items: filteredCustomCategories.map((cc) => {
                return {
                  token_id: cc,
                  label: cc
                }
              })
            }
          : []

      const defaultOpts = filteredDefaultCategories.length
        ? {
            groupLabel: <div className="mt-3">Default categories</div>,
            items: filteredDefaultCategories.map((dc) => {
              return {
                token_id: dc,
                label: dc
              }
            })
          }
        : []

      const allCategories = showAllCategories
        ? [
            {
              token_id: '',
              label: 'All categories'
            }
          ]
        : []

      return [...allCategories, ...[customOpts], ...[defaultOpts]]
    }, [custom_expense_categories, filterByQuery, showAllCategories, view])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onAutocompleteChange = useCallback(
      debounce((value) => {
        setQuery(value as string)
      }, 300),
      []
    )

    return (
      <Autosuggest
        ref={ref}
        value={value}
        onChange={(_value) => {
          onChange?.(_value as string)
        }}
        emptySlot={
          hasTeam && !isFetching && !expense_categories?.length ? (
            <div className="card bg-white flex flex-col justify-center items-center gap-2">
              <p className="text-sm text-center">
                {query
                  ? 'No categories found'
                  : 'Your team does not have any categories yet.'}
              </p>
            </div>
          ) : isFetching ? (
            <div className="card bg-white flex flex-col justify-center items-center gap-2">
              <p className="text-sm text-center">Loading...</p>
            </div>
          ) : undefined
        }
        name="tags"
        wrapperClassName={wrapperClassName}
        className={cn(
          'w-full cursor-pointer',
          variant === 'default' ? '' : 'outline-0! hover:outline-1!'
        )}
        placeholder="Category"
        onBlurOverrideId="categories-filter-btn"
        menuVariant="solid"
        variant={variant}
        status={isFetching ? 'pending' : 'idle'}
        disabled={disabled}
        readOnly={readOnly}
        onInputChange={(_value) => {
          onAutocompleteChange(_value as string)
        }}
        // eslint-disable-next-line
        // @ts-ignore
        options={hasTeam ? teamOptions : noTeamOptions}
      />
    )
  }
)

export default CategoryOption
